import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  IconButton,
  TextField,
} from '@mui/material'
import { FC, useState } from 'react'
import DenyIcon from '@/assets/icons/actions/deny.svg'
import CustomImageCard from '../../card/CustomImageCard'
import DeleteIcon from '@mui/icons-material/Delete'
import { uploadFile } from '@/api'

type Attachment = {
  id: number
  fileName: string
  presignedUrl: string
  fileType: string
  attachmentType: string
}

interface PurchaseRequestDataProps {
  data: {
    purchaseRequestNumber: string | number
    attachments: Attachment[]
  }
  disabled: boolean
  ticketId: number
  index: number
  onChange: (
    index: number,
    field: string,
    value: string | boolean | number | Attachment | Attachment[],
  ) => void
  onDelete: (index: number) => void
  handleAddAttachment: (
    index: number,
    value: Attachment,
    parentIndex?: number,
  ) => void
  handleDeleteAttachment: (attachmentIndex: number, index: number) => void
}

export const PurchaseRequestData: FC<PurchaseRequestDataProps> = ({
  data,
  index,
  onChange,
  onDelete,
  handleAddAttachment,
  handleDeleteAttachment,
  ticketId,
  disabled,
}) => {
  const [loading, setLoading] = useState(false)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setLoading(true)
    if (!file) {
      console.warn('No file selected')
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    try {
      const { data } = await uploadFile({
        ticketId: ticketId,
        attachmentType: 'PR_QUOTATION',
        formData,
      })

      const { id, fileName } = data.data.attachment
      const { presignedUrl, fileType } = data.data

      handleAddAttachment(index, {
        id,
        fileName,
        presignedUrl,
        attachmentType: 'PR_QUOTATION',
        fileType: fileType,
      })
    } catch (error) {
      console.error('File upload failed:', error)
    } finally {
      setLoading(false)
      e.target.value = ''
    }
  }
  return (
    <Grid2
      container
      gap={2}
      justifyContent="space-evenly"
      borderRadius={2}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr .2fr auto' },
        marginBottom: 2,
      }}
    >
      {/* Delete Button  */}
      <Grid2 container alignItems="flex-start" gap={0}>
        <IconButton
          onClick={() => {
            onDelete(index)
          }}
          disabled={disabled}
          sx={{
            // visibility: disabled ? 'hidden' : null,
            visibility: disabled ? 'hidden' : null,
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          <img src={DenyIcon} alt="Delete" width={24} height={24} />
        </IconButton>
      </Grid2>

      {/* Purchase Request Number */}
      <Grid2>
        <TextField
          label="PR Number"
          value={data.purchaseRequestNumber}
          size="small"
          disabled={disabled}
          required
          error={!data.purchaseRequestNumber}
          helperText={
            !data.purchaseRequestNumber ? 'This field is required' : null
          }
          fullWidth
          onChange={(e) => {
            onChange(index, 'purchaseRequestNumber', e.target.value)
          }}
        />
      </Grid2>

      {/* Attach Button */}
      {!disabled && (
        <Grid2
          container
          direction="row"
          alignItems="flex-start"
          sx={{ height: { xs: 'auto', sm: 50, md: 'auto' } }}
          minWidth={160}
        >
          <Button
            variant="contained"
            component="label"
            disabled={disabled}
            sx={{
              width: '100%',
              background: '#484554',
              minWidth: 100,
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Attach'
            )}
            <input
              type="file"
              hidden
              multiple
              accept="image/*,application/pdf"
              onChange={handleFileUpload}
            />
          </Button>
        </Grid2>
      )}

      {/* Attachments */}
      <Grid2>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: { xs: 'auto', sm: 50, md: 40 },
            width: '100%',
            minWidth: '140px',
          }}
        >
          {(Array.isArray(data.attachments) &&
            data.attachments.map((item, attachmentIndex) => (
              <Box
                key={attachmentIndex}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <CustomImageCard
                  src={item.presignedUrl}
                  attachmentNumber={item.fileName}
                  attachmentType={item.fileType}
                  attachmentName={item.fileName}
                  cardMaxHeight={{ xs: 40, sm: 40, md: 40 }}
                  cardWidth={{ xs: '100%', sm: 70, md: 80 }}
                  cardMinHeight={40}
                  imageMaxHeight={15}
                />
                <IconButton disabled={disabled}>
                  <DeleteIcon
                    onClick={() => {
                      handleDeleteAttachment(attachmentIndex, index)
                    }}
                    sx={{
                      fontSize: 20,
                      color: 'inherit',
                      visibility: disabled ? 'hidden' : null,
                    }}
                  />
                </IconButton>
              </Box>
            ))) ??
            null}
        </Box>
      </Grid2>
    </Grid2>
  )
}
